import { Fragment } from 'react';
import {
  FilterBar,
  ListEmpty,
  MoreInOtherMonths,
  NumberedPager,
  useListUrlState,
  rangeForPreset,
  windowLabel,
  inRange,
  applySearch,
  sortBy,
  paginate,
  DEFAULT_HISTORY_PRESET,
  type HistoryDatePreset,
} from '@/components/lists';
import type { Doc } from '../types';
import { DC_DOC_TYPE_FILTER, dayGroupLabel } from '../historyMock';

interface Props {
  /** `queue` = Documents pill (Needs you + Today). `filed` = Filed documents pill. */
  view: 'queue' | 'filed';
  /** Needs-you queue — always shown in full (§2). */
  needs: Doc[];
  /** Today's handled documents — the small live set. */
  today: Doc[];
  /** The big filed history (§9). */
  history: Doc[];
  selectedId?: string;
  onSelect: (id: string) => void;
}

const PAGE_SIZE = 50;

function docText(d: Doc): string {
  return `${d.title} ${d.kindLabel} ${d.patient ?? ''} ${d.payerName ?? ''} ${d.checkNo ?? ''} ${d.claimNo ?? ''} ${d.reference ?? ''} ${d.source} ${d.wentToLabel ?? ''}`;
}

/** A held document (unknown sender): arrival, sender, pages and what ELVA thinks it is (D250). */
function HeldRow({ d, selected, onSelect }: { d: Doc; selected: boolean; onSelect: (id: string) => void }) {
  const h = d.held;
  if (!h) return null;
  const pageWord = `${String(h.pages)} page${h.pages === 1 ? '' : 's'}`;
  return (
    <button type="button" className={`dc-row${selected ? ' on' : ''}`} aria-current={selected || undefined} onClick={() => { onSelect(d.id); }}>
      <span className="dc-rtop"><span className="dc-kind">From an unknown sender</span><span className="dc-meta">{h.arrived}</span></span>
      <span className="dc-rtitle" style={{ display: 'block' }}>{h.via === 'fax' ? 'Fax from' : 'Email from'} {h.sender}</span>
      <span className="dc-meta" style={{ display: 'block', marginTop: 2 }}>{pageWord} · {h.looksLike}</span>
      <span className="dc-rnote" style={{ display: 'block' }}>
        {h.deadline ? <span className="dc-badge b-danger">Deadline {h.deadline.label}</span> : <span className="dc-badge b-warn">Held, nothing read yet</span>}
      </span>
    </button>
  );
}

function Row({ d, selected, onSelect }: { d: Doc; selected: boolean; onSelect: (id: string) => void }) {
  if (d.held && d.status === 'needs') return <HeldRow d={d} selected={selected} onSelect={onSelect} />;
  return (
    <button type="button" className={`dc-row${selected ? ' on' : ''}`} aria-current={selected || undefined} onClick={() => { onSelect(d.id); }}>
      <span className="dc-rtop"><span className="dc-kind">{d.kindLabel}</span><span className="dc-meta">{d.source}</span></span>
      <span className="dc-rtitle" style={{ display: 'block' }}>{d.title}</span>
      <span className="dc-rnote" style={{ display: 'block' }}>
        {d.status === 'needs'
          ? <span className="dc-badge b-warn">A person needs to read it</span>
          : <span className="dc-meta">{d.wentToLabel}</span>}
      </span>
    </button>
  );
}

export function DocList({ view, needs, today, history, selectedId, onSelect }: Props) {
  const [s, patch] = useListUrlState({ q: '', range: DEFAULT_HISTORY_PRESET, type: '', page: '1' });
  const q = s.q;
  const preset = s.range as HistoryDatePreset;
  const typeFilter = s.type;
  const page = Math.max(1, Number.parseInt(s.page, 10) || 1);
  const win = rangeForPreset(preset);

  if (view === 'queue') {
    return (
      <aside className="dc-list" aria-label="Documents">
        {needs.length > 0 ? (
          <>
            <h2 className="dc-grp">Needs you · {needs.length}</h2>
            {needs.map((d) => <Row key={d.id} d={d} selected={selectedId === d.id} onSelect={onSelect} />)}
          </>
        ) : null}
        {today.length > 0 ? (
          <>
            <h2 className="dc-grp">Today · {today.length}</h2>
            {today.map((d) => <Row key={d.id} d={d} selected={selectedId === d.id} onSelect={onSelect} />)}
          </>
        ) : null}
        {needs.length === 0 && today.length === 0 ? (
          <p className="dc-meta" style={{ padding: 16 }}>No documents came in today. Older ones are under Filed documents.</p>
        ) : null}
      </aside>
    );
  }

  // Filed: type → search → window → sort newest first → page, then day-group the page.
  const byType = typeFilter ? history.filter((d) => d.kind === typeFilter) : history;
  const searched = applySearch(byType, q, docText);
  const inWindow = searched.filter((d) => inRange(d.receivedIso ?? '', win));
  const sorted = sortBy(inWindow, (d) => d.receivedIso ?? '', -1);
  const pageRows = paginate(sorted, page, PAGE_SIZE);
  const outsideCount = q.trim() !== '' ? searched.length - inWindow.length : 0;

  const groups: { day: string; rows: Doc[] }[] = [];
  for (const d of pageRows) {
    const day = dayGroupLabel(d.receivedIso ?? '');
    const last = groups[groups.length - 1];
    if (last?.day === day) last.rows.push(d);
    else groups.push({ day, rows: [d] });
  }

  const typeSelect = (
    <select
      className="lx-select"
      value={typeFilter}
      aria-label="Document type"
      onChange={(e) => { patch({ type: e.target.value, page: '1' }); }}
    >
      <option value="">All types</option>
      {DC_DOC_TYPE_FILTER.map((t) => (
        <option key={t.value} value={t.value}>{t.label}</option>
      ))}
    </select>
  );

  return (
    <aside className="dc-list" aria-label="Filed documents">
      <div className="dc-histfilters">
        <FilterBar
          search={q}
          onSearch={(v) => { patch({ q: v, page: '1' }); }}
          searchPlaceholder="Search patient, payer, check, claim, reference or type"
          dateRange={preset}
          onDateRange={(v) => { patch({ range: v, page: '1' }); }}
        >
          {typeSelect}
        </FilterBar>
      </div>

      {pageRows.length === 0 ? (
        <ListEmpty
          query={q.trim() === '' ? undefined : q}
          windowLabel={windowLabel(preset)}
          {...(q.trim() !== '' && preset !== 'all-time'
            ? { onSearchAllTime: () => { patch({ range: 'all-time', page: '1' }); } }
            : {})}
          {...(q.trim() === '' ? { message: `Nothing was filed in ${windowLabel(preset)}.` } : {})}
        />
      ) : (
        <>
          {groups.map((g) => (
            <Fragment key={g.day}>
              <h3 className="dc-daygrp">{g.day}</h3>
              {g.rows.map((d) => <Row key={d.id} d={d} selected={selectedId === d.id} onSelect={onSelect} />)}
            </Fragment>
          ))}
          <div className="dc-histfoot">
            <MoreInOtherMonths count={outsideCount} onShowAllTime={() => { patch({ range: 'all-time', page: '1' }); }} />
            <NumberedPager
              page={page}
              pageSize={PAGE_SIZE}
              total={sorted.length}
              onPage={(p) => { patch({ page: String(p) }); }}
            />
          </div>
        </>
      )}
    </aside>
  );
}
